import { useEffect, useRef, useState } from 'react';
import { TouchPad } from '../../components/TouchPad';
import { arcadeAudio } from '../../platform/audio';
import { fitCanvas, loadImages } from '../../platform/canvas';
import { Input } from '../../platform/input';
import type { GameProps } from '../../platform/types';

const W = 960;
const H = 540;

type Job = 'idle' | 'chop' | 'grill' | 'plate' | 'serve' | 'mop';
interface Cust { id: number; x: number; wait: number; max: number; dish: Dish; served: boolean }
interface Ticket { dish: Dish; stage: Job; hold: boolean }
type Dish = 'burger' | 'salad' | 'coffee' | 'combo';

const RECIPES: Record<Dish, Job[]> = {
  burger: ['chop', 'grill', 'plate'],
  salad: ['chop', 'plate'],
  coffee: ['plate'],
  combo: ['chop', 'grill', 'plate'],
};

export function NightServiceGame({ onExit, onResult }: GameProps) {
  const ref = useRef<HTMLCanvasElement>(null);
  const input = useRef(new Input());
  const [night, setNight] = useState(1);
  const [paused, setPaused] = useState(false);
  const [shop, setShop] = useState(false);
  const [over, setOver] = useState<{ score: number; won: boolean } | null>(null);
  const money = useRef(0);
  const stats = useRef({ speed: 1, grill: 1, patience: 1, mop: 1 });

  useEffect(() => {
    void arcadeAudio.playMusic('night', '/assets/music/Italian-Mom.ogg');
    return () => arcadeAudio.stopMusic();
  }, []);

  useEffect(() => {
    if (shop || over || paused) return;
    const canvas = ref.current!;
    const ctx = fitCanvas(canvas, W, H)!;
    const inp = input.current.attach(canvas);
    let dead = false;
    const cook = { x: 480, y: 300, job: 'idle' as Job, t: 0, hands: null as Ticket | null };
    const stations: Array<{ name: Job; x: number; y: number; r: number }> = [
      { name: 'chop', x: 180, y: 220, r: 46 },
      { name: 'grill', x: 360, y: 180, r: 46 },
      { name: 'plate', x: 560, y: 180, r: 46 },
      { name: 'serve', x: 760, y: 260, r: 60 },
      { name: 'mop', x: 140, y: 400, r: 40 },
    ];
    const custs: Cust[] = [];
    const spills: Array<{ x: number; y: number }> = [];
    let spawn = 0;
    let left = 4 + night;
    let served = 0;
    let failed = 0;
    let acc = 0;
    let id = 1;
    let imgs = new Map<string, HTMLImageElement>();
    void loadImages({
      fridge: '/assets/furn/kitchenFridge_SE.png',
      stove: '/assets/furn/kitchenStove_SE.png',
      sink: '/assets/furn/kitchenSink_SE.png',
      hero: '/assets/plat/char/character_yellow_idle.png',
      c1: '/assets/plat/char/character_pink_idle.png',
      c2: '/assets/plat/char/character_green_idle.png',
      c3: '/assets/plat/char/character_beige_idle.png',
    }).then((m) => { imgs = m; });

    const menu: Dish[] = night < 3 ? ['coffee', 'salad', 'burger'] : ['coffee', 'salad', 'burger', 'combo'];

    let last = performance.now();
    let raf = 0;
    const loop = (now: number) => {
      if (dead) return;
      const dt = Math.min(0.04, (now - last) / 1000);
      last = now;
      tick(dt);
      draw();
      raf = requestAnimationFrame(loop);
    };

    const tick = (dt: number) => {
      acc += dt;
      const ax = inp.axis();
      cook.x = Math.max(40, Math.min(W - 40, cook.x + ax.x * 210 * stats.current.speed * dt));
      cook.y = Math.max(80, Math.min(H - 40, cook.y + ax.y * 210 * stats.current.speed * dt));
      spawn += dt;
      if (left > 0 && spawn > Math.max(3.2 - night * 0.15, 1.6)) {
        spawn = 0; left -= 1;
        const dish = menu[(Math.random() * menu.length) | 0];
        custs.push({ id: id++, x: 80 + custs.length * 70, wait: 22 * stats.current.patience, max: 22 * stats.current.patience, dish, served: false });
      }
      for (const c of custs) {
        if (c.served) continue;
        c.wait -= dt;
        if (c.wait <= 0) {
          c.served = true;
          failed += 1;
          spills.push({ x: 200 + Math.random() * 400, y: 360 });
        }
      }
      if (cook.job !== 'idle') {
        cook.t -= dt;
        if (cook.t <= 0) {
          if (cook.hands) {
            const rec = RECIPES[cook.hands.dish];
            const i = rec.indexOf(cook.job);
            if (i >= 0 && cook.hands.stage === rec[i]) {
              const next = rec[i + 1];
              cook.hands.stage = (next ?? 'serve') as Job;
              arcadeAudio.blip(500, 0.06);
            }
          }
          if (cook.job === 'mop') spills.pop();
          cook.job = 'idle';
        }
      } else if (inp.pressed('space') || inp.actions.a) {
        inp.actions.a = false;
        inp.keys[' '] = false;
        const near = stations.find((s) => (s.x - cook.x) ** 2 + (s.y - cook.y) ** 2 < s.r ** 2);
        if (near) {
          if (near.name === 'chop' && !cook.hands) {
            const c = custs.find((x) => !x.served && RECIPES[x.dish][0] !== 'plate');
            if (c) {
              cook.hands = { dish: c.dish, stage: 'chop', hold: true };
              cook.job = 'chop';
              cook.t = 1.1;
            }
          } else if (near.name === 'plate' && !cook.hands) {
            const c = custs.find((x) => !x.served && RECIPES[x.dish][0] === 'plate');
            if (c) {
              cook.hands = { dish: c.dish, stage: 'plate', hold: true };
              cook.job = 'plate';
              cook.t = 0.8;
            }
          } else if (near.name === 'grill' && cook.hands?.stage === 'grill') {
            cook.job = 'grill'; cook.t = 1.4 / stats.current.grill;
          } else if (near.name === 'plate' && cook.hands && (cook.hands.stage === 'plate' || RECIPES[cook.hands.dish][0] === 'plate')) {
            if (!cook.hands) return;
            if (RECIPES[cook.hands.dish][0] === 'plate' && cook.hands.stage === 'chop') cook.hands.stage = 'plate';
            cook.job = 'plate'; cook.t = 0.8;
          } else if (near.name === 'serve' && cook.hands?.stage === 'serve') {
            const c = custs.find((x) => !x.served && x.dish === cook.hands!.dish);
            if (c) {
              c.served = true;
              served += 1;
              money.current += 12 + night * 2;
              cook.hands = null;
              arcadeAudio.blip(720, 0.08, 'triangle', 0.1);
            }
          } else if (near.name === 'mop' && spills.length) {
            cook.job = 'mop'; cook.t = 0.9 / stats.current.mop;
          }
        }
      }

      const open = custs.filter((c) => !c.served).length;
      if (left <= 0 && open === 0) {
        dead = true;
        if (night >= 10) {
          onResult({
            score: money.current,
            won: failed < 8,
            stamps: { clear: true, skill: failed <= 3, mastery: failed === 0 },
          });
          setOver({ score: money.current, won: failed < 8 });
        } else setShop(true);
      }
    };

    const draw = () => {
      ctx.fillStyle = '#1b1210';
      ctx.fillRect(0, 0, W, H);
      const sky = ctx.createLinearGradient(0, 0, 0, 130);
      sky.addColorStop(0, '#2a1a28');
      sky.addColorStop(1, '#3a2418');
      ctx.fillStyle = sky;
      ctx.fillRect(0, 0, W, 130);
      ctx.fillStyle = '#3a1c14';
      ctx.fillRect(720, 16, 96, 78);
      ctx.fillStyle = '#ff8a3d';
      ctx.globalAlpha = 0.35;
      ctx.fillRect(728, 24, 80, 62);
      ctx.globalAlpha = 1;
      ctx.fillStyle = '#0c0705';
      ctx.beginPath();
      ctx.roundRect(36, 16, 168, 78, 8);
      ctx.fill();
      ctx.strokeStyle = '#e0b15b';
      ctx.stroke();
      ctx.fillStyle = '#ff8a3d';
      ctx.font = '700 22px Fraunces, serif';
      ctx.fillText('OPEN', 78, 64);
      for (let x = 0; x < W; x += 28) {
        for (let y = 130; y < 400; y += 28) {
          ctx.fillStyle = ((x + y) / 28) % 2 === 0 ? '#3a2a22' : '#2d201a';
          ctx.fillRect(x, y, 28, 28);
        }
      }
      ctx.fillStyle = '#4a3428';
      ctx.fillRect(0, 390, W, 150);
      ctx.fillStyle = '#2a1c16';
      ctx.fillRect(80, 150, 540, 18);
      ctx.fillStyle = '#6b3f28';
      ctx.fillRect(700, 228, 190, 86);
      ctx.fillStyle = '#3a2418';
      ctx.fillRect(708, 208, 174, 22);
      ctx.fillStyle = '#e0b15b';
      ctx.fillRect(708, 228, 174, 4);
      const drawImg = (k: string, x: number, y: number, s = 78) => {
        const im = imgs.get(k);
        if (im) ctx.drawImage(im, x - s / 2, y - s / 2, s, s);
        else { ctx.fillStyle = '#ff8a3d'; ctx.fillRect(x - 24, y - 24, 48, 48); }
      };
      drawImg('fridge', 180, 210);
      drawImg('stove', 360, 180);
      drawImg('sink', 560, 180);
      ctx.fillStyle = '#fff4df';
      ctx.font = '600 11px Figtree, sans-serif';
      ctx.fillText('CHOP', 162, 262);
      ctx.fillText('GRILL', 342, 236);
      ctx.fillText('PLATE', 542, 236);
      ctx.fillText('SERVE', 762, 328);
      ctx.fillText('MOP', 122, 430);
      for (const s of spills) {
        ctx.fillStyle = '#3ee0c555';
        ctx.beginPath(); ctx.ellipse(s.x, s.y, 22, 12, 0, 0, 7); ctx.fill();
      }
      const faces = ['hero', 'c1', 'c2', 'c3'];
      custs.filter((x) => !x.served).forEach((c, i) => {
        const face = imgs.get(faces[i % faces.length]);
        if (face) ctx.drawImage(face, c.x - 16, 36, 32, 38);
        else {
          ctx.fillStyle = '#e0b15b';
          ctx.beginPath(); ctx.arc(c.x, 58, 14, 0, 7); ctx.fill();
        }
        ctx.fillStyle = '#0008';
        ctx.fillRect(c.x - 18, 80, 36, 5);
        ctx.fillStyle = c.wait < 6 ? '#ff5d8f' : '#3ee0c5';
        ctx.fillRect(c.x - 18, 80, 36 * (c.wait / c.max), 5);
        ctx.fillStyle = '#fff4df';
        ctx.fillText(c.dish, c.x - 16, 100);
      });
      drawImg('hero', cook.x, cook.y, 52);
      if (cook.hands) {
        ctx.fillStyle = '#e0b15b';
        ctx.fillText(cook.hands.dish + ' → ' + cook.hands.stage, cook.x - 34, cook.y - 32);
      }
      if (cook.job !== 'idle') {
        ctx.fillStyle = '#ff8a3d';
        ctx.fillRect(cook.x - 20, cook.y + 24, 40 * Math.max(0, cook.t), 4);
      }
      ctx.fillStyle = 'rgba(12,7,5,0.7)';
      ctx.fillRect(12, 8, 320, 28);
      ctx.fillStyle = '#fff4df';
      ctx.font = '13px Figtree, sans-serif';
      ctx.fillText(`Night ${night}   $${money.current}   served ${served}   lost ${failed}`, 20, 28);
    };

    raf = requestAnimationFrame(loop);
    return () => {
      dead = true;
      cancelAnimationFrame(raf);
      inp.dispose();
    };
  }, [night, shop, paused, over, onResult]);

  const buy = (k: keyof typeof stats.current, cost: number) => {
    if (money.current < cost) return;
    money.current -= cost;
    stats.current[k] += 0.18;
    setShop(false);
    setNight((n) => n + 1);
  };

  return (
    <div className="game-root nightservice">
      <canvas ref={ref} width={W} height={H} />
      <TouchPad
        input={input.current}
        extra={[{ id: 'use', label: 'Use', on: () => { input.current.actions.a = true; }, off: () => { input.current.actions.a = false; } }]}
      />
      {shop && (
        <div className="overlay">
          <div className="panel">
            <h2>Night {night} closed</h2>
            <p>${money.current} in the tin. Buy one thing.</p>
            <div className="choices">
              <button type="button" className="play" onClick={() => buy('speed', 20)}>Sneakers — move faster</button>
              <button type="button" className="play" onClick={() => buy('grill', 20)}>Hotter grill</button>
              <button type="button" className="play" onClick={() => buy('patience', 25)}>Free bread — guests wait</button>
              <button type="button" className="play" onClick={() => buy('mop', 15)}>Better mop</button>
              <button type="button" className="ghost" onClick={() => { setShop(false); setNight((n) => n + 1); }}>Skip</button>
            </div>
          </div>
        </div>
      )}
      {over && (
        <div className="overlay">
          <div className="panel">
            <h2>{over.won ? 'The diner lives' : 'Keys on the counter'}</h2>
            <p>Bank ${over.score}</p>
            <button type="button" className="play" onClick={onExit}>Lobby</button>
          </div>
        </div>
      )}
    </div>
  );
}
